window.dn = window.dn || {};

/**
 * Methods for processing text.
 */
(function (text, $, undefined)
{
    /**
     * Return true if both strings begin with the same {minWords} words.
     * 
     * E.g. "This is a test" and "This is a test of the emergency broadcast system" would return true if minWords is 4 or less.
     */
    text.HasMatchingPrefix = function(str1, str2, minWords) 
    {
        var words1 = str1.split(' ').slice(0, minWords).join(' ');
        var words2 = str2.split(' ').slice(0, minWords).join(' ');

        return words1 === words2;
    };

    /**
     * Return true if the string contains both lower and upper case characters.
     * 
     * @param {string} text - The string to check.
     * @returns {boolean} - True if the string contains both lower and upper case characters.
     */
    text.IsMixedCase = function(text) 
    {
        text = String(text);

        if (text === null || text.trim() === "") 
        {
            return false;
        }

        return dn.constants.RegEx.LowerCase.test(text) && dn.constants.RegEx.UpperCase.test(text);
    }

    /**
     *  Joins a list into a human sentence, separated by `separator`, except for the last two elements,
     *  which are separated by `lastSeparator`, unless there's only 2 elements, in which case they 
     *  are separated by `divider`.
     *  
     *  By default, if you just call this on an IEnumerable with no parameters, it will handle it 
     *  with traditional Oxford comma, and separating two elements with " and ".
     *  
     *  Based on [this](https://stackoverflow.com/a/44886666/650558) with my own tweaks to make it
     *  easier to use, and to deal with the situation where there are only 2 elements.
     *  </summary>
     *  <param name="values">The IEnumerable to be joined together into a string.</param>
     *  <param name="separator">The separator to use between all elements except for the last two.</param>
     *  <param name="lastSeparator">The separator to use between the last two elements.</param>
     *  <typeparam name="T">The type to join, anything that StringBuilder can turn into a string.</typeparam>
     *  <returns>A string combining the given IEnumerable into a human-readable list.</returns>
     */
    text.JoinForHumans = function(list, separator, lastSeparator, divider)
    {
        if (list.length === 1) return list[0];
        var firsts = list.slice(0, list.length - 1);
        var last = list[list.length - 1];
        return firsts.join(separator || ', ') + (firsts.length > 1 ? (lastSeparator || ', and ') : (divider || ' and ')) + last;
    };

    /**
     * Given a string, make it suitable for use as a URL slug.
     * 
     * Currently this means making it lowercase, replacing multiple spaces/dashes with a single dash,
     * and then removing multiple non-letters, numbers, and remaining whitespace.
     * 
     * Keep this code in sync with DN.Utils.Text.NormalizeForUrl() C# code.
     * 
     * @param   {string} text       - Potential URL slug string.
     * @param   {string} maxLength  - Maximum length of the URL slug.
     * @returns {string}            - URL slug suitable string.
     */
    text.NormalizeForUrl = function(text, maxLength) 
    {
        if (!text)
        {
            return text;
        }

        text = text
            .replace(dn.constants.RegEx.Apostrophes, "")
            .replace(dn.constants.RegEx.NonLettersAndNumbersTrim, "")
            .replace(dn.constants.RegEx.NonLettersAndNumbers, "-");

        if (text.lengthInTextElements() > maxLength)
        {
            text.SubstringByTextElements(0, maxLength).replace(dn.constants.RegEx.NonLettersAndNumbersTrim, "");
        }

        return text.toLowerCase();
    };

    /**
     * Quote strings that are going to be used in HTML attributes, so that things like
     * ampersands, quotation marks, apostrophes, and angle brackets are HTML-escaped.
     * 
     * @param {string} str - The string to convert.
     * @returns The converted/escaped string, ready to be used as an HTML attribute.
     */
    text.QuoteAttribute = function(str) 
    {
        return ('' + str)
            .replace(/&/g, '&amp;')
            .replace(/'/g, '&apos;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    };

    /**
     * Reduce a URL to a nice, shorter url for display purposes. Typically
     * you would call this from a Regex replacement when manipulating A HREFs.
     *  
     * E.g. convert https://www.apple.com/this/is/a/fake/url.html
     *           to apple.com/this/is/a/fake…
     * 
     * If `noHtml` is false (the default), we'll insert HTML zero-width space
     * encoded characters to prevent these shortened urls from being auto-linked.
     * 
     * NOTE: Keep in sync with C# DN.Utils.Text.ShortenUrlForDisplay().
     * 
     * @param (string)  url - A URL that might need to be shortened.
     * @param (bool)    noHtml - If true, we won't add the zero-width &8203; escapes.
     * @returns A URL shortened for DISPLAY purposes only.
     */
    text.ShortenUrlForDisplay = function(url, noHtml)
    {
        return url.replace
        (
            dn.constants.RegEx.UrlPartial,
            function(match, protocol, partialUrl, extension)
            {
                var shortened = null;

                if (extension && extension.indexOf("htm") < 0)
                {
                    // has a trailing suffix (other than some form of .html) that we 
                    // can use to make the URL more intuitive
                    shortened = partialUrl.replace(dn.constants.RegEx.EndsWithNonLettersNumbers, "") + "…" + extension;
                }
                else
                {
                    // Just go with the main shortened portion of URL, no suffix.
                    // If the partial URL turns out to be the entire URL, do nothing, 
                    // otherwise append ellipsis.
                    shortened = partialUrl.replace(dn.constants.RegEx.EndsWithNonLettersNumbers, "") + (!match.endsWith(partialUrl) ? "…" : "");
                }

                if (!noHtml)
                {
                    // Zero-width space, to prevent email messages from linking these shortened faux-urls
                    shortened = shortened.replace(/\./g, "&#8203;.&#8203;");
                }

                return shortened;
            }
        );
    };

    /**
     * Works just like native `string.split`, except that it will return an empty array if the 
     * string is null/empty, or if the string is just the separator.
     */
    text.Split = function(str, separator, limit)
    {
        if (str === null || str === "" || str === undefined)
        {
            return [];
        }

        // Split the string and strip out any empty entries.
        return str.split(separator, limit).filter(function (x) { return x !== ""; });
    };

    /**
     * Return a Title Cased version of a string. First we lowercase the incoming text, then we pass it into the 
     * CultureInfo's TextInfo class to get a title-cased version. Next we pass that through our regex that lets us know 
     * which strings should be returned to UPPERCASE. Then a final pass to handle any strings that should be lowercase 
     * (such as a, an, the, for, etc.). Finally, we capitalize any letter that appears at the beginning of the string, 
     * or after a colon and a space.
     * 
     * NOTE: Please keep this logic in-sync with DN.Extensions.ToTitleCase() C# extension method.
     * 
     * @param (string)  text - A string to be title-cased
     * @returns A title-cased string which has better linguistic support than standard .NET methods, and supports some 
     * auto industry standard acronyms (e.g. ASE, OEM, etc) and conventions (such as things with numbers/letters mixed
     * together being UPPERCASE, like VINs, etc).
     */
    text.ToTitleCase = function(text) 
    {
        text = String(text);

        if (text === null || text.trim() === "")
        {
            return text;
        }

        var lowerText = text.toLowerCase();

        if (String.prototype.matchAll)
        {
            // Browser supports matchAll (i.e. not IE11), so let's go the extra yard
            // 
            // Replace any strings we found in the original text which should still be uppercase.
            // For example if someone typed "This is an ICE vehicle" then when we're done the string
            // should have "this is an ICE vehicle" instead of "this is an ice vehicle".
            var matches = text.matchAll(dn.constants.RegEx.AcronymsIfCapitalized);

            for (var i = 0; i < matches.length; i++) 
            {
                var match = matches[i];
                // Replace the corresponding part in lowerText with the original matched text
                lowerText = lowerText.slice(0, match.index) + match[0] + lowerText.slice(match.index + match[0].length);
            }
        }

        var lower = lowerText.split(dn.constants.RegEx.NonWordCharacters);

        for (var i = 0; i < lower.length; i++) 
        {
            lower[i] = lower[i].charAt(0).toUpperCase() + lower[i].slice(1);
        }

        return lower.join('') // join with empty string since the array contains all the separators (NonWordCharacters) already!
        .replaceAll
        (
            // TitleCaseUpperCase guarantees any word that should be uppercase is uppercase, such as number+letter mixtures, and industry acronyms, like "ASE", "OEM", etc.
            dn.constants.RegEx.TitleCaseUpperCase, 
            function(match)
            {
                return match.toUpperCase();
            }
        )
        .replaceAll
        (
            // TitleCaseLowerCase guarantees any word that should be lowercase is lowercase, such as "a", "an", "the", etc.
            dn.constants.RegEx.TitleCaseLowerCase,
            function(match)
            {
                return match.toLowerCase();
            }
        )
        .replaceAll
        (
            // PhraseStart guarantees the first letter after a colon and a space is capitalized.
            dn.constants.RegEx.PhraseStart,
            function(match, p1, p2)
            {
                return (p1 + p2.toUpperCase());
            }
        )
        .replaceAll
        (
            // FirstLetter guarantees the first word is capitalized, and any word after a colon and a space is capitalized.
            dn.constants.RegEx.FirstLetter,
            function(match, p1, p2)
            {
                return (p1 + p2.toUpperCase());
            }
        )
        .replace
        (
            // LastWord guarantees the last word of the title is capitalized, even if other rules failed, as is customary.
            dn.constants.RegEx.LastWord,
            function(match, p1, p2, p3, p4)
            {
                return (p1 + p2.toUpperCase() + p3 + p4);
            }
        );
    };

}(window.dn.text = window.dn.text || {}, jQuery));
